import { Head } from 'vite-react-ssg'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  langToHtml,
  langToLocale,
  type Language,
} from '../i18n/languages'

interface SEOProps {
  titleKey?: string
  descriptionKey?: string
  path?: string
  noindex?: boolean
}

export default function SEO({ titleKey, descriptionKey, path, noindex = false }: SEOProps) {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()

  const title = titleKey ? t(titleKey) : t('seo.home.title')
  const description = descriptionKey ? t(descriptionKey) : t('seo.home.description')
  const routePath = path ?? pathname
  const normalizedPath = routePath.startsWith('/') ? routePath : `/${routePath}`
  const baseUrl = `https://buenas-studio.com${normalizedPath}`
  const lang = (i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE) as Language
  const locale = langToLocale[lang] || langToLocale[DEFAULT_LANGUAGE]
  const image = 'https://buenas-studio.com/assets/logo-rot.png'
  const ogType = normalizedPath === '/' ? 'website' : 'article'

  // Self-referencing canonical that matches the active language variant,
  // so each hreflang cluster page points to itself.
  const activeUrl = `${baseUrl}?lang=${lang}`

  const hreflangUrls = LANGUAGES.map((language) => ({
    hrefLang: langToHtml[language],
    href: `${baseUrl}?lang=${language}`,
  }))

  return (
    <Head>
      <html lang={langToHtml[lang] || langToHtml[DEFAULT_LANGUAGE]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={activeUrl} />
      {hreflangUrls.map((item) => (
        <link key={item.hrefLang} rel="alternate" hrefLang={item.hrefLang} href={item.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}?lang=${DEFAULT_LANGUAGE}`} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="buenas studio" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={activeUrl} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={activeUrl} />
    </Head>
  )
}
